import { MutationResult } from "react-apollo";

import { ConfirmButtonTransitionState } from "./components/ConfirmButton";
import { IFilter } from "./components/Filter";
import { MultiAutocompleteChoiceType } from "./components/MultiAutocompleteSelectField";
import { User_userPermissions } from "./fragments/types/User";

export interface UserError {
  field: string | null;
  message?: string;
}

export interface DialogProps {
  open: boolean;
  onClose: () => void;
}

export interface ListSettings<TColumn extends string = string> {
  columns?: TColumn[];
  rowNumber: number;
}

export enum ListViews {
  ANNOUNCEMENT_LIST = "ANNOUNCEMENT_LIST",
  APPS_LIST = "APPS_LIST",
  ATTRIBUTE_LIST = "ATTRIBUTE_LIST",
  BANNER_LIST = "BANNER_LIST",
  BULK_VOUCHER_LIST = "BULK_VOUCHER_LIST",
  BRANDGROUPS_LIST = "BRANDGROUP_LIST",
  CATEGORY_LIST = "CATEGORY_LIST",
  CLUSTER_LIST = "CLUSTER_LIST",
  COLLECTION_LIST = "COLLECTION_LIST",
  COUPON_LIST = "COUPON_LIST",
  COUPON_REDEMPTION_LIST = "COUPON_REDEMPTION_LIST",
  CUSTOMER_LIST = "CUSTOMER_LIST",
  DRAFT_LIST = "DRAFT_LIST",
  INVENTORY_LIST = "INVENTORY_LIST",
  MALL_LIST = "MALL_LIST",
  NAVIGATION_LIST = "NAVIGATION_LIST",
  ORDER_LIST = "ORDER_LIST",
  PAGES_LIST = "PAGES_LIST",
  PLUGINS_LIST = "PLUGIN_LIST",
  PRODUCT_LIST = "PRODUCT_LIST",
  REMITTANCE_LIST = "REMITTANCE_LIST",
  PERMISSION_GROUP_LIST = "PERMISSION_GROUP_LIST",
  PRODUCT_TYPE_LIST = "PRODUCT_TYPE_LIST",
  SALES_LIST = "SALES_LIST",
  SHIPPING_METHODS_LIST = "SHIPPING_METHODS_LIST",
  STAFF_MEMBERS_LIST = "STAFF_MEMBERS_LIST",
  VOUCHER_LIST = "VOUCHER_LIST",
  WAREHOUSE_LIST = "WAREHOUSE_LIST",
  WEBHOOK_LIST = "WEBHOOK_LIST",
  BRANDS_LIST = "BRANDS_LIST",
  PRODUCT_STOCKS = "PRODUCT_STOCKS",
  STORE_REPORT_STOCKS = "STORE_REPORT_STOCKS",
  REPORTS_LIST = "REPORTS_LIST",
  PAYOUT_DETAILS_LIST = "PAYOUT_DETAILS_LIST",
  STORE_LIST = "STORE_LIST",
  ORDER_DETAILS_LIST = "ORDER_DETAILS_LIST",
  DISPATCH_LIST = "DISPATCH_LIST",
  APPCONFIG_LIST = "APPCONFIG_LIST",
  APPCONFIGDETAIL_LIST = "APPCONFIGDETAIL_LIST",
  PAYMENT_EVENTS = "PAYMENT_EVENTS",
  COUPON_COLLECTION_LIST = "COUPON_COLLECTION_LIST",
  COUPON_COLLECTION_ASSIGNED_COUPONS_LIST = "COUPON_COLLECTION_ASSIGNED_COUPONS_LIST",
  AUDIT_LOGS_LIST = "AUDIT_LOGS_LIST"
}

export interface ListProps<TColumns extends string = string> {
  disabled?: boolean;
  pageInfo?: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  settings?: ListSettings<TColumns>;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onRowClick: (id: string) => () => void;
  onUpdateListSettings?: (
    key: keyof ListSettings<TColumns>,
    value: any
  ) => void;
  onListSettingsReset?: () => void;
}

export interface SortPage<TSortKey extends string> {
  sort: Sort<TSortKey>;
  onSort: (field: TSortKey, id?: string) => void;
}

/**
 * @param toggle Will be use to change status of item
 * @param isChecked Returns true for ids of chosen items
 * @param selected  Number of chosen items.
 */

export interface ListActionsWithoutToolbar {
  toggle: (id: string) => void;
  toggleAll: (items: React.ReactNodeArray, selected: number) => void;
  isChecked: (id: string) => boolean;
  selected: number;
}
export type TabListActions<
  TToolbars extends string
> = ListActionsWithoutToolbar &
  Record<TToolbars, React.ReactNode | React.ReactNodeArray>;
export interface ListActions extends ListActionsWithoutToolbar {
  toolbar: React.ReactNode | React.ReactNodeArray;
}
export interface PageListProps<TColumns extends string = string>
  extends ListProps<TColumns> {
  brands?: any;
  defaultSettings?: ListSettings<TColumns>;
  onAdd: () => void;
}

export interface SearchProps {
  onSearchChange: (value: string) => void;
}
export interface SearchPageProps extends SearchProps {
  initialSearch: string;
}
export interface FilterPageProps<TKeys extends string, TOpts extends object>
  extends FilterProps<TKeys>,
    SearchPageProps,
    TabPageProps {
  filterOpts: TOpts;
}

export interface FilterMenuProps<TKeys extends string, TOpts extends object>
  extends FilterProps<TKeys> {
  filterOpts: TOpts;
}
export interface FilterProps<TKeys extends string> {
  currencySymbol: string;
  onFilterChange: (filter: IFilter<TKeys>) => void;
}

export interface TabPageProps {
  currentTab: number;
  tabs: any[];
  onAll?: () => void;
  onTabChange: (tab: number) => void;
  onTabDelete: () => void;
  onTabSave: () => void;
  download?: any;
}

export interface PartialMutationProviderOutput<
  TData extends {} = {},
  TVariables extends {} = {}
> {
  opts: MutationResult<TData> & MutationResultAdditionalProps;
  mutate: (variables: TVariables) => void;
}

export interface Node {
  id: string;
}

export type Pagination = Partial<{
  after: string;
  before: string;
}>;

export type Dialog<TDialog extends string> = Partial<{
  action: TDialog;
}>;
export type ActiveTab<TTab extends string = string> = Partial<{
  activeTab: TTab;
}>;
export type Filters<TFilters extends string> = Partial<
  Record<TFilters, string>
>;
export type FiltersWithMultipleValues<TFilters extends string> = Partial<
  Record<TFilters, string[]>
>;
export type FiltersAsDictWithMultipleValues<TFilters extends string> = Partial<
  Record<TFilters, Record<string, string[]>>
>;
export type Search = Partial<{
  query: string;
}>;
export type SingleAction = Partial<{
  id: string;
}>;
export type Sort<TSort extends string = string> = Partial<{
  asc: boolean;
  sort: TSort;
}>;
export type BulkAction = Partial<{
  ids: string[];
}>;

export interface ReorderEvent {
  oldIndex: number;
  newIndex: number;
}
export type ReorderAction = (event: ReorderEvent) => void;

export interface FetchMoreProps {
  loading: boolean;
  hasMore: boolean;
  onFetchMore: () => void;
}

export type TabActionDialog = "save-search" | "delete-search" | "view-malls";

export interface UserPermissionProps {
  userPermissions: User_userPermissions[];
}
export type Assign = Partial<{
  assign: string;
}>;
export interface MutationResultAdditionalProps {
  status: ConfirmButtonTransitionState;
}

export type MinMax = Record<"min" | "max", string>;

export interface FilterOpts<T> {
  active: boolean;
  value: T;
}

export interface AutocompleteFilterOpts
  extends FetchMoreProps,
    SearchPageProps {
  choices: MultiAutocompleteChoiceType[];
  displayValues: MultiAutocompleteChoiceType[];
}
